import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { appendTranscript, speechSupported, startSpeechRecognition } from "../../lib/speechPrompt";
import { useAuth } from "../../lib/authStore";
import { useToasts } from "../../components/Toasts";
import { captureSelectionShots } from "./captureShots";
import { canUseEditMode } from "./editAccess";
import {
  EDIT_CHIPS,
  PRODUCT_LABELS,
  buildEditRequestPayload,
  climbToEditable,
  describeElement,
  elementsInRect,
  pageContext,
} from "./elementCapture";
import "./EditModeHost.css";

const CHIP_STARTERS = new Set(EDIT_CHIPS.map(([, label]) => `${label}: `));

function errorText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg).filter(Boolean).join(" ");
  return "Could not send the edit request.";
}

function normalizeRect(x1, y1, x2, y2) {
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

function panelStyle(rect) {
  const width = 340;
  const margin = 12;
  let left = (rect?.x || 0) + (rect?.width || 0) + margin;
  if (left + width > window.innerWidth - margin) {
    left = Math.max(margin, (rect?.x || 0) - width - margin);
  }
  let top = rect?.y || margin;
  if (top + 300 > window.innerHeight - margin) top = Math.max(margin, window.innerHeight - 320);
  return { left, top, width };
}

export default function EditModeHost() {
  const user = useAuth((state) => state.user);
  const scope = useAuth((state) => state.scope);
  const allowed = canUseEditMode(user, scope);
  const navigate = useNavigate();
  const push = useToasts((state) => state.push);
  const [enabled, setEnabled] = useState(false);
  const [regionArmed, setRegionArmed] = useState(false);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState([]);
  const [marquee, setMarquee] = useState(null);
  const [text, setText] = useState("");
  const [chip, setChip] = useState(null);
  const [listening, setListening] = useState(false);
  const [micNote, setMicNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const textRef = useRef(null);
  const recognitionRef = useRef(null);
  const dragRef = useRef(null);
  const ignoreClickRef = useRef(false);
  const selectedRef = useRef([]);
  const regionArmedRef = useRef(false);
  selectedRef.current = selected;
  regionArmedRef.current = regionArmed;

  const stopMic = useCallback(() => {
    try { recognitionRef.current?.abort?.() || recognitionRef.current?.stop?.(); } catch { /* already stopped */ }
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const exit = useCallback(() => {
    stopMic();
    setEnabled(false);
    setRegionArmed(false);
    setHover(null);
    setSelected([]);
    setMarquee(null);
    setError("");
  }, [stopMic]);

  useEffect(() => {
    document.documentElement.classList.toggle("edit-mode-on", allowed && enabled);
    return () => document.documentElement.classList.remove("edit-mode-on");
  }, [allowed, enabled]);

  useEffect(() => {
    if (!allowed) {
      setEnabled(false);
      return undefined;
    }
    const onKey = (event) => {
      if (event.altKey && event.code === "KeyE" && !event.repeat) {
        event.preventDefault();
        event.stopPropagation();
        setEnabled((current) => !current);
        return;
      }
      if (event.key === "Escape" && enabled) {
        event.preventDefault();
        event.stopPropagation();
        exit();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [allowed, enabled, exit]);

  useEffect(() => {
    if (!allowed || !enabled) return undefined;
    const rememberHover = (event) => {
      if (dragRef.current?.moved) return;
      const element = climbToEditable(event.target);
      if (!element) {
        setHover(null);
        return;
      }
      const described = describeElement(element);
      setHover({ label: described.label, rect: described.bounding_box });
    };
    const onPointerDown = (event) => {
      if (!regionArmedRef.current || event.button !== 0) return;
      if (event.target?.closest?.("[data-edit-chrome]")) return;
      dragRef.current = { x: event.clientX, y: event.clientY, moved: false, shift: event.shiftKey };
    };
    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag) {
        rememberHover(event);
        return;
      }
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 8) return;
      drag.moved = true;
      setHover(null);
      setMarquee(normalizeRect(drag.x, drag.y, event.clientX, event.clientY));
    };
    const applyElements = (elements, { additive, region }) => {
      const next = elements.map((element) => ({
        key: describeElement(element).selector || friendlyFallback(element),
        element,
        snapshot: describeElement(element, { region }),
      }));
      setSelected((current) => {
        if (!additive) return next;
        const keys = new Set(current.map((item) => item.key));
        return [...current, ...next.filter((item) => !keys.has(item.key))].slice(0, 12);
      });
    };
    const onPointerUp = (event) => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag?.moved) return;
      const rect = normalizeRect(drag.x, drag.y, event.clientX, event.clientY);
      setMarquee(null);
      ignoreClickRef.current = true;
      if (rect.width < 8 || rect.height < 8) return;
      const elements = elementsInRect(rect);
      if (elements.length) {
        applyElements(elements, { additive: drag.shift, region: rect });
        return;
      }
      const regionSelection = {
        key: `region-${Math.round(rect.x)}-${Math.round(rect.y)}`,
        element: null,
        snapshot: {
          label: "Highlighted region",
          tag: "region",
          selector: "",
          test_id: null,
          edit_name: null,
          data_attributes: {},
          component_chain: [],
          text_snippet: "",
          bounding_box: {
            x: Math.round(rect.x),
            y: Math.round(rect.y),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
          },
          region: {
            x: Math.round(rect.x),
            y: Math.round(rect.y),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
          },
        },
      };
      setSelected((current) => (drag.shift ? [...current, regionSelection].slice(0, 12) : [regionSelection]));
    };
    const onClick = (event) => {
      if (event.target?.closest?.("[data-edit-chrome]")) return;
      if (ignoreClickRef.current) {
        ignoreClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const element = climbToEditable(event.target);
      if (!element) return;
      const described = describeElement(element);
      const item = { key: described.selector || friendlyFallback(element), element, snapshot: described };
      setSelected((current) => {
        if (event.shiftKey) {
          const exists = current.some((entry) => entry.element === element || entry.key === item.key);
          if (exists) return current.filter((entry) => entry.element !== element && entry.key !== item.key);
          return [...current, item].slice(0, 12);
        }
        return [item];
      });
    };
    const onPointerCancel = () => {
      dragRef.current = null;
      setMarquee(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointermove", onPointerMove, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("pointercancel", onPointerCancel, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointermove", onPointerMove, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("pointercancel", onPointerCancel, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [allowed, enabled]);

  useEffect(() => {
    if (selected.length) textRef.current?.focus();
  }, [selected]);

  useEffect(() => () => stopMic(), [stopMic]);

  const anchor = selected[selected.length - 1]?.snapshot?.bounding_box;
  const micAvailable = speechSupported();

  const chooseChip = (id, label) => {
    setChip(id);
    setText((current) => (!current.trim() || CHIP_STARTERS.has(current) ? `${label}: ` : current));
    textRef.current?.focus();
  };

  const dictate = () => {
    if (listening) {
      stopMic();
      return;
    }
    setMicNote("");
    const recognition = startSpeechRecognition({
      onStart: () => setListening(true),
      onEnd: () => setListening(false),
      onError: () => setMicNote("Voice didn't catch that. Edit the text, then send."),
      onResult: (transcript) => {
        setText((current) => appendTranscript(current, transcript));
        setMicNote("Review the transcript before sending.");
      },
    });
    if (!recognition) {
      setMicNote("Microphone transcription isn't available in this browser. Type the request instead.");
      return;
    }
    recognitionRef.current = recognition;
  };

  const send = async () => {
    const request = text.trim();
    if (!request || sending) return;
    setSending(true);
    setError("");
    stopMic();
    const selections = selectedRef.current.map((item) => (
      item.element?.isConnected ? describeElement(item.element, { region: item.snapshot?.region }) : item.snapshot
    ));
    let shots = { screenshot_note: null };
    try {
      shots = await captureSelectionShots(selections.map((item) => item.bounding_box));
    } catch (captureError) {
      const message = String(captureError?.message || captureError || "unknown error").slice(0, 180);
      shots = { screenshot_note: `Screenshot capture failed: ${message}` };
    }
    try {
      await api.post("/edit-requests", buildEditRequestPayload({ text: request, chip, selections, shots }));
      setText("");
      setChip(null);
      setSelected([]);
      setMicNote("");
      setSavedNote("Request saved");
      push("Edit request sent", "ok");
      window.setTimeout(() => setSavedNote(""), 2400);
    } catch (sendError) {
      setError(errorText(sendError));
    } finally {
      setSending(false);
    }
  };

  if (!allowed) return null;

  return (
    <div className="em-root" data-edit-chrome="true">
      {enabled && hover ? (
        <div className="em-box is-hover" style={boxStyle(hover.rect)} data-edit-chrome="true">
          <span className="em-label">{hover.label}</span>
        </div>
      ) : null}
      {enabled ? selected.map((item) => (
        <div key={item.key} className="em-box is-selected" style={boxStyle(item.snapshot.bounding_box)} data-edit-chrome="true">
          <span className="em-label">{item.snapshot.label}</span>
        </div>
      )) : null}
      {marquee ? <div className="em-box is-region" style={boxStyle(marquee)} data-edit-chrome="true" /> : null}

      {enabled && selected.length ? (
        <form
          className="em-panel"
          style={panelStyle(anchor)}
          data-edit-chrome="true"
          onSubmit={(event) => { event.preventDefault(); send(); }}
        >
          <header>
            <b>{selected.length === 1 ? selected[0].snapshot.label : `${selected.length} elements`}</b>
            <small>{PRODUCT_LABELS[pageContext().product] || "App"}</small>
            <button type="button" onClick={() => setSelected([])} aria-label="Clear selection">Clear</button>
          </header>
          <textarea
            ref={textRef}
            rows={3}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="What should change?"
            aria-label="Edit request"
          />
          <div className="em-chips">
            {EDIT_CHIPS.map(([id, label]) => (
              <button key={id} type="button" className={chip === id ? "is-on" : ""} onClick={() => chooseChip(id, label)}>
                {label}
              </button>
            ))}
          </div>
          {micNote ? <p className="em-note">{micNote}</p> : null}
          {error ? <p className="em-error">{error}</p> : null}
          <footer>
            <button
              type="button"
              className={listening ? "is-on" : ""}
              onClick={dictate}
              disabled={!micAvailable && !listening}
              aria-label={listening ? "Stop microphone" : "Dictate into the request"}
              title={micAvailable ? "Transcribe into the text box. Nothing is sent until you press Send." : "Microphone transcription isn't available in this browser"}
            >
              {listening ? "Stop mic" : "Mic"}
            </button>
            <button type="submit" className="em-send" disabled={!text.trim() || sending}>
              {sending ? "Sending…" : "Send"}
            </button>
          </footer>
        </form>
      ) : null}

      <div className="em-dock" data-edit-chrome="true">
        {savedNote ? <span className="em-saved">{savedNote}</span> : null}
        {enabled ? (
          <button
            type="button"
            className={regionArmed ? "is-on" : ""}
            aria-pressed={regionArmed}
            onClick={() => setRegionArmed((current) => !current)}
          >
            {regionArmed ? "Drag to highlight" : "Highlight region"}
          </button>
        ) : null}
        <button type="button" onClick={() => navigate("/commandlinc/edit-requests")}>
          Edit requests
        </button>
        <button
          type="button"
          className={`em-toggle${enabled ? " is-on" : ""}`}
          aria-pressed={enabled}
          aria-label={enabled ? "Exit edit mode" : "Edit mode"}
          title="Edit mode (Alt+E)"
          onClick={() => (enabled ? exit() : setEnabled(true))}
        >
          {enabled ? "Exit edit" : "Edit"}
        </button>
      </div>
      {enabled && regionArmed ? <p className="em-region-hint" data-edit-chrome="true">Drag a rectangle. Shift-drag adds to the selection. Esc exits.</p> : null}
    </div>
  );
}

function friendlyFallback(element) {
  return `${element?.tagName || "el"}-${textSnippetSafe(element)}`;
}

function textSnippetSafe(element) {
  return String(element?.textContent || "").replace(/\s+/g, " ").trim().slice(0, 24);
}

function boxStyle(rect) {
  if (!rect) return { display: "none" };
  return {
    left: rect.x,
    top: rect.y,
    width: rect.width,
    height: rect.height,
  };
}
